/* A task's Responsible Party (Duncan 2026-10-21: the schedule is where a sub is put on a job). Typing opens
   the subs in Subcontractor Mgmt, each with its trades and insurance; picking one links the task to that
   sub, so the task shows on their Jobs tab (and, later, in their portal). Anything else can still be typed
   as plain text, as before. A sub marked Do not use is shown but can't be picked. The server keeps the same
   rules (routes/tasks.js responsibleParty). */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getSubcontractorOptions } from '../../api';
import { can } from '../../permissions';
import { insuranceState, fmtShortDay, todayYmd } from '../../lib/subcontractors';

let cache = null; // one fetch per page: the list rarely changes
const loadOptions = () => {
  if (!cache) {
    cache = getSubcontractorOptions().then((r) => r.subcontractors || []).catch((e) => { cache = null; throw e; });
  }
  return cache;
};

const signedInUser = () => {
  try { return JSON.parse(localStorage.getItem('e3_user') || 'null'); } catch { return null; }
};

const MAX_ROWS = 8;

/* The host's input style with its padding as four sides, so the icon's room can be added and taken away
   again (React drops a side it last set over a `padding` shorthand, leaving it at 0). */
function withRoom(style, left, right) {
  const { padding, ...rest } = style || {};
  const p = String(padding == null ? '0 10px' : padding).trim().split(/\s+/);
  const [t, r = t, b = t, l = r] = p;
  return { ...rest, paddingTop: t, paddingBottom: b, paddingLeft: left == null ? l : left, paddingRight: right == null ? r : right };
}

/** A sub's note on the list and under the field: its trades (plain), and what needs saying about its
    standing or insurance, coloured: { trades, issue: { text, tone } | null, text } (text = both, for labels). */
export function subNote(s, today = todayYmd()) {
  const trades = (s.trades || []).join(', ');
  const ins = insuranceState(s.insurance_expiry, today);
  const issue = s.standing === 'do_not_use' ? { text: 'Do not use', tone: 'var(--danger)' }
    : ins === 'none' ? { text: 'No COI Recorded', tone: 'var(--danger)' } // Duncan 2026-10-21
      : ins === 'expired' ? { text: `insurance expired ${fmtShortDay(s.insurance_expiry, today)}`, tone: 'var(--danger)' }
        : ins === 'expiring' ? { text: `insurance expires ${fmtShortDay(s.insurance_expiry, today)}`, tone: 'var(--warning)' }
          : null;
  return { trades, issue, text: [trades, issue && issue.text].filter(Boolean).join(' · ') };
}

/** The subs to list for what's typed: name or trade, the closest first. */
export function matchSubs(subs, typed) {
  const q = String(typed || '').trim().toLowerCase();
  if (!q) return subs.slice(0, MAX_ROWS);
  const scored = [];
  for (const s of subs) {
    const name = s.name.toLowerCase();
    const at = name.indexOf(q);
    const trade = (s.trades || []).some((t) => t.toLowerCase().startsWith(q));
    if (at < 0 && !trade) continue;
    scored.push([at === 0 ? 0 : at > 0 ? 1 : 2, s]);
  }
  return scored.sort((a, b) => a[0] - b[0]).map(([, s]) => s).slice(0, MAX_ROWS);
}

/* subsOnly: only a sub can be chosen (no plain-text row) -- the Subcontractors tab's Move / Assign pickers;
   excludeId leaves one sub out of the list (the sub the tasks are on now). */
export default function ResponsiblePartyField({ value, subcontractorId, onChange, inputStyle, onFocus, onBlur, placeholder = 'Crew or vendor', subsOnly = false, excludeId = null, ariaLabel }) {
  const [subs, setSubs] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [box, setBox] = useState(null);
  const inputRef = useRef(null);
  const today = todayYmd();
  const canOpen = useMemo(() => can(signedInUser(), 'subs.manage'), []);

  useEffect(() => {
    let live = true;
    loadOptions().then((list) => { if (live) setSubs(list); }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  const linked = subcontractorId && subs ? subs.find((s) => s.id === Number(subcontractorId)) : null;
  const text = value || '';
  const rows = useMemo(() => (subs ? matchSubs(subs.filter((s) => s.id !== excludeId), linked ? '' : text) : []), [subs, text, linked, excludeId]);
  const exact = subs && subs.some((s) => s.name.toLowerCase() === text.trim().toLowerCase());
  const asText = !subsOnly && text.trim() && !linked && !exact ? text.trim() : null;
  // What the keyboard moves through: pickable subs, then the plain-text row.
  const choices = [...rows.filter((s) => s.standing !== 'do_not_use').map((s) => ({ sub: s })), ...(asText ? [{ text: asText }] : [])];

  // The list floats over the dialog (which scrolls), under the field.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = inputRef.current && inputRef.current.getBoundingClientRect();
      if (!r) return;
      const width = Math.max(r.width, 340);
      setBox({ left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), top: r.bottom + 4, width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  const pick = (choice) => {
    if (!choice) return;
    if (choice.sub) onChange({ responsible_party: choice.sub.name, subcontractor_id: choice.sub.id });
    else onChange({ responsible_party: choice.text, subcontractor_id: null });
    setOpen(false);
  };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      const n = choices.length;
      if (n) setActive((i) => (e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n));
    } else if (e.key === 'Enter' && open && choices.length) {
      e.preventDefault();
      pick(choices[Math.min(active, choices.length - 1)]);
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation(); // the list closes, not the dialog
      setOpen(false);
    }
  };
  const activeChoice = choices[Math.min(active, choices.length - 1)];
  const isActive = (c) => activeChoice && (c.sub ? activeChoice.sub === c.sub : activeChoice.text === c.text);
  const host = typeof document !== 'undefined' ? (document.querySelector('.app') || document.body) : null;
  const note = linked ? subNote(linked, today) : null;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        {linked && (
          <span aria-hidden="true" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--accent-text)', display: 'flex' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
            </svg>
          </span>
        )}
        <input
          ref={inputRef}
          type="text" name="responsible_party" autoComplete="off"
          role="combobox" aria-expanded={open} aria-autocomplete="list" aria-label={ariaLabel}
          value={text}
          placeholder={placeholder}
          // Typing over a picked sub makes it plain text again, until a sub is picked.
          onChange={(e) => { onChange({ responsible_party: e.target.value, subcontractor_id: null }); setOpen(true); setActive(0); }}
          onKeyDown={onKeyDown}
          onFocus={(e) => { setOpen(true); setActive(0); if (onFocus) onFocus(e); }}
          onBlur={(e) => { setOpen(false); if (onBlur) onBlur(e); }}
          style={withRoom(inputStyle, linked ? 32 : null, linked ? 30 : null)}
        />
        {linked && (
          <button
            type="button" aria-label="Clear the subcontractor"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { onChange({ responsible_party: '', subcontractor_id: null }); if (inputRef.current) inputRef.current.focus(); }}
            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', padding: 4, display: 'flex' }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        )}
      </div>
      {linked && (
        // The link is one piece: on this line when it fits, else whole on the next (Duncan 2026-10-21).
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 10, rowGap: 2, fontSize: 12, color: 'var(--text-3)', marginTop: 5, lineHeight: 1.4 }}>
          <span>
            Subcontractor
            {note.trades ? ` · ${note.trades}` : null}
            {note.issue ? <> · <span style={{ color: note.issue.tone }}>{note.issue.text}</span></> : null}
          </span>
          {canOpen && (
            <a href={`/subcontractors/${linked.id}`} target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--accent-text)', textDecoration: 'none', whiteSpace: 'nowrap' }}>Manage Sub Details</a>
          )}
        </div>
      )}
      {open && box && host && (subs || failed) && (rows.length || asText || failed) ? createPortal(
        <div
          role="listbox"
          onMouseDown={(e) => e.preventDefault()} // keep the field focused while choosing
          style={{
            position: 'fixed', left: box.left, top: box.top, width: box.width, zIndex: 10050,
            background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)', overflow: 'hidden', fontSize: 13.5,
          }}
        >
          {failed ? (
            <div style={{ padding: '9px 12px', color: 'var(--text-3)' }}>Couldn’t load the subcontractors.{subsOnly ? '' : ' Plain text still works.'}</div>
          ) : (
            <>
              {rows.length > 0 && (
                <div style={{ padding: '7px 12px 3px', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4, var(--text-3))' }}>Subcontractors</div>
              )}
              {rows.map((s) => {
                const off = s.standing === 'do_not_use';
                const n = subNote(s, today);
                const on = !off && isActive({ sub: s });
                return (
                  <div
                    key={s.id} role="option" aria-selected={on} aria-disabled={off}
                    aria-label={`${s.name}${n.text ? `, ${n.text}` : ''}${off ? ', can’t be picked' : ''}`}
                    title={off ? `${s.name} is marked Do not use in Subcontractor Mgmt` : undefined}
                    onClick={() => { if (!off) pick({ sub: s }); }}
                    onMouseEnter={() => { if (!off) setActive(choices.findIndex((c) => c.sub === s)); }}
                    style={{
                      display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 12px',
                      cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.55 : 1,
                      background: on ? 'var(--accent-soft)' : 'transparent',
                    }}
                  >
                    <span style={{ color: on ? 'var(--accent-text)' : 'var(--text-1)', flex: '0 0 auto', maxWidth: '62%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                    <span title={n.text} style={{ color: 'var(--text-3)', fontSize: 12, flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {n.trades}{n.trades && n.issue ? ' · ' : ''}{n.issue ? <span style={{ color: n.issue.tone }}>{n.issue.text}</span> : null}
                    </span>
                  </div>
                );
              })}
              {asText && (
                <div
                  role="option" aria-selected={isActive({ text: asText })}
                  onClick={() => pick({ text: asText })}
                  onMouseEnter={() => setActive(choices.findIndex((c) => c.text === asText))}
                  style={{
                    padding: '7px 12px', cursor: 'pointer', color: 'var(--text-2)',
                    borderTop: rows.length ? '1px solid var(--border-subtle)' : 'none',
                    background: isActive({ text: asText }) ? 'var(--accent-soft)' : 'transparent',
                  }}
                >
                  Use “{asText}” as plain text
                </div>
              )}
            </>
          )}
        </div>,
        host,
      ) : null}
    </div>
  );
}
